import React, { useState, useEffect } from 'react';
import { CreditCard, Package, AlertCircle, Check, AlertTriangle, Calendar, ArrowDown, XCircle } from 'lucide-react';
import { Card, Badge, Alert } from 'react-bootstrap';
import { useAuth } from '../../../../contexts/AuthContext';
import { API_ENDPOINTS } from "../../../../config/api.config";
import { apiService } from "../../../../services/apiService";
import { SubscriptionUpgradeModal } from "../../../../components/modals/billing/SubscriptionUpgradeModal";
import '../../../../../scss/subscription.scss';
import ContentSkeleton from "@/components/loading/ContentSkeleton.tsx";


interface ApiPlan {
    id: string;
    name: string;
    description: string;
    tier: 'free' | 'starter' | 'professional' | 'enterprise';
    price: string;
    billing_interval: 'monthly' | 'yearly';
    storage_limit_gb: number;
    max_knowledge_bases: number;
    max_queries_per_day: number;
    conversations_limit: number;
    files_limit: number;
    data_sources_limit: number;
    has_api_access: boolean;
    has_web_crawling: boolean;
    has_api_connectors: boolean;
    has_advanced_features: boolean;
    indexed_pages_limit: number;
    trial_period_days: number | null;
    is_active: boolean;
    is_public: boolean;
    sort_order: number;
}

interface PlanFeatures {
    conversations_per_month: number;
    storage_gb: number;
    knowledge_bases: number;
    data_sources: number;
    indexed_pages: number;
    support_level: string;
    api_access: 'none' | 'rest' | 'full';
    description: string;
}

interface Plan {
    id: string;
    name: string;
    description: string;
    price: number;
    billing_interval: 'monthly' | 'yearly';
    trial_period_days: number | null;
    features: PlanFeatures;
}

interface SubscriptionPlan extends Plan {}

interface Subscription {
    id: string;
    status: 'active' | 'past_due' | 'canceled' | 'incomplete';
    current_period_start: string;
    current_period_end: string;
    cancel_at_period_end: boolean;
    plan: SubscriptionPlan;
}

interface DowngradeStateInfo {
    targetPlan: Plan;
    effectiveDate: string;
}

interface CancellationStateInfo {
    effectiveDate: string;
}

interface ApiResponse<T> {
    data: T;
    message?: string;
}

interface CancellationResponse {
    effectiveDate: string;
}

interface DowngradeResponse {
    subscriptionId: string;
    effectiveDate: string;
}

const defaultPlans: Plan[] = [
    {
        id: 'free-trial',
        name: 'Free Trial',
        description: 'Perfect for trying out the service',
        price: 0,
        billing_interval: 'monthly',
        trial_period_days: 14,
        features: {
            conversations_per_month: 100,
            storage_gb: 1,
            knowledge_bases: 1,
            data_sources: 3,
            indexed_pages: 25,
            support_level: 'Community Support',
            api_access: 'none',
            description: 'Perfect for trying out the service'
        }
    },
    {
        id: 'starter',
        name: 'Starter Plan',
        description: 'Perfect for small teams',
        price: 799,
        billing_interval: 'monthly',
        trial_period_days: null,
        features: {
            conversations_per_month: 2000,
            storage_gb: 10,
            knowledge_bases: 3,
            data_sources: 10,
            indexed_pages: 250,
            support_level: 'Email Support',
            api_access: 'none',
            description: 'Perfect for small teams'
        }
    },
    {
        id: 'professional',
        name: 'Professional Plan',
        description: 'Ideal for growing departments',
        price: 1999,
        billing_interval: 'monthly',
        trial_period_days: null,
        features: {
            conversations_per_month: 8000,
            storage_gb: 50,
            knowledge_bases: 10,
            data_sources: 30,
            indexed_pages: 1000,
            support_level: 'Priority Support',
            api_access: 'rest',
            description: 'Ideal for growing departments'
        }
    },
    {
        id: 'enterprise',
        name: 'Enterprise Plan',
        description: 'Built for large organizations',
        price: 4999,
        billing_interval: 'monthly',
        trial_period_days: null,
        features: {
            conversations_per_month: 20000,
            storage_gb: 200,
            knowledge_bases: 30,
            data_sources: 100,
            indexed_pages: 5000,
            support_level: 'Premium Support + SLA',
            api_access: 'full',
            description: 'Built for large organizations'
        }
    }
];

const mapApiPlanToDisplayPlan = (apiPlan: ApiPlan): Plan => {

    const determineApiAccess = (plan: ApiPlan): 'none' | 'rest' | 'full' => {
        if (!plan.has_api_access) return 'none';
        return plan.tier === 'enterprise' ? 'full' : 'rest';
    };

    return {
        id: apiPlan.id,
        name: apiPlan.name,
        description: apiPlan.description,
        price: parseFloat(apiPlan.price),
        billing_interval: apiPlan.billing_interval,
        trial_period_days: apiPlan.trial_period_days,
        features: {
            conversations_per_month: apiPlan.conversations_limit,
            storage_gb: apiPlan.storage_limit_gb,
            knowledge_bases: apiPlan.max_knowledge_bases,
            data_sources: apiPlan.data_sources_limit,
            indexed_pages: apiPlan.indexed_pages_limit || 0, // Fallback to 0 if not provided
            support_level: getSupportLevel(apiPlan.tier),
            api_access: determineApiAccess(apiPlan),
            description: apiPlan.description
        }
    };
};

const getSupportLevel = (tier: string): string => {
    switch (tier) {
        case 'enterprise':
            return 'Priority Support';
        case 'professional':
        case 'starter':
            return 'Email Support';
        default:
            return 'Community Support';
    }
};

const SubscriptionPage: React.FC = () => {
    const auth = useAuth();
    const [plans, setPlans] = useState<Plan[]>(defaultPlans);
    const [subscription, setSubscription] = useState<Subscription | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
    const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false);
    const [selectedNewPlan, setSelectedNewPlan] = useState<Plan | null>(null);
    const [pendingDowngrade, setPendingDowngrade] = useState<DowngradeStateInfo | null>(null);
    const [pendingCancellation, setPendingCancellation] = useState<CancellationStateInfo | null>(null);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    useEffect(() => {
        console.log('Banner debug:', {
            showCancellationBanner: !!pendingCancellation,
            showDowngradeBanner: !!pendingDowngrade && !pendingCancellation,
            pendingCancellation,
            pendingDowngrade
        });
    }, [pendingCancellation, pendingDowngrade]);

    useEffect(() => {
        console.log('Current states:', {
            pendingCancellation,
            pendingDowngrade,
            isProcessing,
            subscription
        });
    }, [pendingCancellation, pendingDowngrade, isProcessing, subscription]);

    useEffect(() => {
        fetchSubscriptionData();
        checkPendingChanges();
    }, []);


    const checkPendingChanges = () => {
        const storedDowngrade = localStorage.getItem('pendingPlanDowngrade');
        const storedCancellation = localStorage.getItem('pendingCancellation');

        if (storedDowngrade) {
            try {
                const downgradeData = JSON.parse(storedDowngrade) as DowngradeStateInfo;
                setPendingDowngrade(downgradeData);
            } catch (error) {
                console.error('Error parsing stored downgrade data:', error);
                localStorage.removeItem('pendingPlanDowngrade');
            }
        }

        if (storedCancellation) {
            try {
                const cancellationData = JSON.parse(storedCancellation) as CancellationStateInfo;
                setPendingCancellation(cancellationData);
            } catch (error) {
                console.error('Error parsing stored cancellation data:', error);
                localStorage.removeItem('pendingCancellation');
            }
        }
    };

    const fetchSubscriptionData = async () => {
        try {
            setLoading(true);
            setError(null);

            console.log('Fetching subscription data...');

            const [plansRes, subscriptionRes, pendingChangesRes] = await Promise.all([
                apiService.get<{ data: ApiPlan[] }>(API_ENDPOINTS.BILLING.PLANS),
                apiService.get<{ data: Subscription }>(API_ENDPOINTS.BILLING.SUBSCRIPTION.GET),
                apiService.get<{
                    data: {
                        downgrade?: { plan_id: string; effective_date: string } | null;
                        cancellation?: { effective_date: string } | null;
                    }
                }>(API_ENDPOINTS.BILLING.SUBSCRIPTION.PENDING_CHANGES)
            ]);

            // Debug log raw API responses
            console.log('API Responses:', {
                plans: plansRes?.data,
                subscription: subscriptionRes?.data,
                pendingChanges: pendingChangesRes?.data
            });

            // Handle plans data
            if (plansRes?.data) {
                // Debug log each plan before mapping
                console.log('Raw plans before mapping:', plansRes.data);

                const mappedPlans = plansRes.data.map(mapApiPlanToDisplayPlan);
                console.log('Mapped plans:', mappedPlans);

                setPlans(mappedPlans);
            }

            // Handle subscription data
            if (subscriptionRes?.data) {
                setSubscription(subscriptionRes.data);
                setSelectedPlan(subscriptionRes.data.plan?.id || null);
            }

            // Handle pending changes
            if (pendingChangesRes?.data) {
                const { downgrade, cancellation } = pendingChangesRes.data;

                console.log('Processing pending changes:', { downgrade, cancellation });

                // Handle cancellation first because it takes precedence
                if (cancellation) {
                    console.log('Setting cancellation state');
                    const cancellationInfo: CancellationStateInfo = {
                        effectiveDate: cancellation.effective_date
                    };
                    setPendingCancellation(cancellationInfo);
                    localStorage.setItem('pendingCancellation', JSON.stringify(cancellationInfo));

                    // Clear any downgrade state since we have a cancellation
                    setPendingDowngrade(null);
                    localStorage.removeItem('pendingPlanDowngrade');
                } else {
                    console.log('Clearing cancellation state');
                    setPendingCancellation(null);
                    localStorage.removeItem('pendingCancellation');

                    // Only handle downgrade if there's no cancellation
                    if (downgrade) {
                        console.log('Setting downgrade state');
                        const targetPlan = plansRes.data.find((p: ApiPlan) => p.id === downgrade.plan_id);
                        if (targetPlan) {
                            const downgradeInfo: DowngradeStateInfo = {
                                targetPlan: mapApiPlanToDisplayPlan(targetPlan),
                                effectiveDate: downgrade.effective_date
                            };
                            setPendingDowngrade(downgradeInfo);
                            localStorage.setItem('pendingPlanDowngrade', JSON.stringify(downgradeInfo));
                        }
                    } else {
                        console.log('Clearing downgrade state');
                        setPendingDowngrade(null);
                        localStorage.removeItem('pendingPlanDowngrade');
                    }
                }
            } else {
                // If no pending changes response, clear all pending states
                console.log('No pending changes, clearing all states');
                setPendingCancellation(null);
                setPendingDowngrade(null);
                localStorage.removeItem('pendingCancellation');
                localStorage.removeItem('pendingPlanDowngrade');
            }

        } catch (err) {
            console.error('Error fetching subscription data:', err);
            setError(err instanceof Error ? err.message : 'Failed to load subscription data');

            // On error, maintain existing states from localStorage if available
            const storedCancellation = localStorage.getItem('pendingCancellation');
            const storedDowngrade = localStorage.getItem('pendingPlanDowngrade');

            if (storedCancellation) {
                try {
                    setPendingCancellation(JSON.parse(storedCancellation));
                } catch (e) {
                    console.error('Error parsing stored cancellation:', e);
                    localStorage.removeItem('pendingCancellation');
                }
            }

            if (storedDowngrade) {
                try {
                    setPendingDowngrade(JSON.parse(storedDowngrade));
                } catch (e) {
                    console.error('Error parsing stored downgrade:', e);
                    localStorage.removeItem('pendingPlanDowngrade');
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePlanSelect = (planId: string) => {
        if (planId === selectedPlan || pendingCancellation) return;

        const newPlan = plans.find(p => p.id === planId);
        if (!newPlan) return;

        const currentPlan = subscription?.plan;
        if (!currentPlan) return;

        setSelectedNewPlan(newPlan);

        if (newPlan.price > currentPlan.price) {
            setShowUpgradeModal(true);
        } else {
            handleDowngrade(newPlan);
        }
    };

    const handleDowngrade = async (targetPlan: Plan) => {
        if (pendingCancellation) {
            setError('Cannot downgrade while cancellation is pending. Please cancel the cancellation request first.');
            return;
        }

        try {
            setError(null);
            setIsProcessing(true);

            const response = await apiService.post<ApiResponse<DowngradeResponse>>(
                API_ENDPOINTS.BILLING.SUBSCRIPTION.DOWNGRADE,
                {
                    planId: targetPlan.id,
                    subscriptionId: subscription?.id
                }
            );

            if (!response?.data?.effectiveDate) {
                throw new Error('Invalid response from server');
            }

            const downgradeInfo: DowngradeStateInfo = {
                targetPlan,
                effectiveDate: response.data.effectiveDate
            };

            setPendingDowngrade(downgradeInfo);
            localStorage.setItem('pendingPlanDowngrade', JSON.stringify(downgradeInfo));
            await fetchSubscriptionData(); // Refresh subscription data

        } catch (err) {
            setPendingDowngrade(null);
            localStorage.removeItem('pendingPlanDowngrade');
            setError(err instanceof Error ? err.message : 'Failed to process downgrade');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleCancelSubscription = async () => {
        if (!subscription?.id) {
            setError('No active subscription found');
            return;
        }

        if (pendingDowngrade) {
            setError('Cannot cancel while downgrade is pending. Please cancel the downgrade request first.');
            return;
        }

        try {
            setIsProcessing(true);
            setError(null);

            console.log('Cancelling subscription:', subscription.id);

            const response = await apiService.post<ApiResponse<CancellationResponse>>(
                API_ENDPOINTS.BILLING.SUBSCRIPTION.CANCEL,
                {
                    subscriptionId: subscription.id
                }
            );

            console.log('Cancel subscription response:', response);

            // Use the effective date from response if available, otherwise use current period end
            const effectiveDate = response?.data?.effectiveDate || subscription.current_period_end;

            const cancellationInfo: CancellationStateInfo = {
                effectiveDate
            };

            setPendingCancellation(cancellationInfo);
            localStorage.setItem('pendingCancellation', JSON.stringify(cancellationInfo));

            // Refresh subscription data to get latest state
            await fetchSubscriptionData();

        } catch (err) {
            console.error('Error cancelling subscription:', err);
            setError(err instanceof Error ? err.message : 'Failed to cancel subscription');

            // Clear any partially set state
            setPendingCancellation(null);
            localStorage.removeItem('pendingCancellation');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleCancelDowngrade = async () => {
        if (!subscription?.id) {
            setError('No active subscription found');
            return;
        }

        try {
            setError(null);
            setIsProcessing(true);

            console.log('Starting downgrade cancellation process', {
                subscriptionId: subscription.id,
                hasPendingDowngrade: !!pendingDowngrade
            });

            // Make the API call to cancel the downgrade
            const response = await apiService.post(
                API_ENDPOINTS.BILLING.SUBSCRIPTION.CANCEL_DOWNGRADE,
                { subscriptionId: subscription.id }
            );

            console.log('Cancel downgrade API response:', response);

            // Clear the pending downgrade state immediately
            setPendingDowngrade(null);
            localStorage.removeItem('pendingPlanDowngrade');

            // Refresh subscription data to get the latest state
            await fetchSubscriptionData();

            console.log('Downgrade cancellation complete');

        } catch (err) {
            console.error('Error in handleCancelDowngrade:', err);
            // If there's an error, try to restore the previous state
            const storedDowngrade = localStorage.getItem('pendingPlanDowngrade');
            if (storedDowngrade) {
                try {
                    setPendingDowngrade(JSON.parse(storedDowngrade));
                } catch (parseError) {
                    console.error('Error parsing stored downgrade:', parseError);
                    localStorage.removeItem('pendingPlanDowngrade');
                }
            }
            setError(err instanceof Error ? err.message : 'Failed to cancel downgrade');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleCancelCancellation = async () => {
        if (!subscription?.id) {
            setError('No active subscription found');
            return;
        }

        try {
            setError(null);
            setIsProcessing(true);

            console.log('Starting cancellation cancellation process', {
                subscriptionId: subscription.id,
                hasPendingCancellation: !!pendingCancellation
            });

            const response = await apiService.post(
                API_ENDPOINTS.BILLING.SUBSCRIPTION.CANCEL_CANCELLATION,
                { subscriptionId: subscription.id }
            );

            console.log('Cancel cancellation API response:', response);

            // Clear the pending cancellation state immediately
            setPendingCancellation(null);
            localStorage.removeItem('pendingCancellation');

            // Fetch fresh subscription data
            await fetchSubscriptionData();

            console.log('Cancellation cancellation complete');

        } catch (err) {
            console.error('Error in handleCancelCancellation:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to cancel cancellation request';
            setError(errorMessage);

            // Restore the previous cancellation state from localStorage if available
            const storedCancellation = localStorage.getItem('pendingCancellation');
            if (storedCancellation) {
                setPendingCancellation(JSON.parse(storedCancellation));
            }
        } finally {
            setIsProcessing(false);
        }
    };

    const getFeatureDifferences = (currentPlan: SubscriptionPlan, targetPlan: Plan): string[] => {
        if (!currentPlan?.features || !targetPlan?.features) {
            return [];
        }

        const differences: string[] = [];

        if (currentPlan.features.conversations_per_month > targetPlan.features.conversations_per_month) {
            differences.push(`Conversations will decrease from ${currentPlan.features.conversations_per_month.toLocaleString()} to ${targetPlan.features.conversations_per_month.toLocaleString()} per month`);
        }

        if (currentPlan.features.storage_gb > targetPlan.features.storage_gb) {
            differences.push(`Storage will decrease from ${currentPlan.features.storage_gb}GB to ${targetPlan.features.storage_gb}GB`);
        }

        if (currentPlan.features.knowledge_bases > targetPlan.features.knowledge_bases) {
            differences.push(`Knowledge base limit will decrease from ${currentPlan.features.knowledge_bases} to ${targetPlan.features.knowledge_bases}`);
        }

        if (currentPlan.features.indexed_pages > targetPlan.features.indexed_pages) {
            differences.push(`Indexed pages limit will decrease from ${currentPlan.features.indexed_pages.toLocaleString()} to ${targetPlan.features.indexed_pages.toLocaleString()}`);
        }

        if (currentPlan.features.api_access !== targetPlan.features.api_access) {
            if (targetPlan.features.api_access === 'none') {
                differences.push('You will lose API access');
            } else if (targetPlan.features.api_access === 'rest' && currentPlan.features.api_access === 'full') {
                differences.push('You will have limited API access (REST only)');
            }
        }

        return differences;
    };

    const renderPendingCancellation = () => {
        console.log('renderPendingCancellation called', { pendingCancellation, isProcessing });
        if (!pendingCancellation) {
            console.log('No pending cancellation, returning null');
            return null;
        }

        const formatDate = (date: string) => {
            return new Date(date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        };

        console.log('Rendering cancellation banner with data:', {
            effectiveDate: pendingCancellation.effectiveDate
        });

        return (
            <Card className="cancellation-banner border-danger mb-4">
                <Card.Header>
                    <div className="d-flex align-items-center">
                        <XCircle size={20} className="text-danger me-2" />
                        <h6 className="mb-0 text-danger">Subscription Cancellation Requested</h6>
                    </div>
                </Card.Header>
                <Card.Body className="p-4">
                    <div className="mb-4">
                        <div className="d-flex align-items-center text-danger mb-3">
                            <AlertTriangle size={20} className="me-2" />
                            <p className="mb-0 fw-medium">
                                Your subscription will be cancelled
                            </p>
                        </div>

                        <div className="d-flex align-items-center text-muted">
                            <Calendar size={20} className="me-2" />
                            <p className="mb-0">
                                Service will end on {formatDate(pendingCancellation.effectiveDate)}
                            </p>
                        </div>
                    </div>

                    <Alert variant="danger" className="mb-4">
                        <div className="d-flex align-items-start">
                            <AlertCircle size={20} className="me-2 mt-1" />
                            <div>
                                <p className="fw-medium mb-2">After cancellation:</p>
                                <ul className="ps-3 mb-0">
                                    <li>You will lose access to all premium features</li>
                                    <li>Your data will be retained for 30 days</li>
                                    <li>You can reactivate your subscription before the end date</li>
                                </ul>
                            </div>
                        </div>
                    </Alert>

                    <div className="d-flex justify-content-end">
                        <button
                            onClick={() => {
                                console.log('Keep Subscription button clicked', {
                                    isPendingCancellation: !!pendingCancellation,
                                    isPendingDowngrade: !!pendingDowngrade,
                                    isProcessing
                                });
                                handleCancelCancellation();
                            }}
                            className="btn btn-outline-danger"
                            disabled={isProcessing}
                            type="button"
                        >
                            {isProcessing ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"
                                          aria-hidden="true"/>
                                    Processing...
                                </>
                            ) : (
                                'Keep Subscription'
                            )}
                        </button>
                    </div>
                </Card.Body>
            </Card>
        );
    };

    const renderPendingDowngrade = () => {
        if (!pendingDowngrade || !subscription?.plan) return null;

        const formatDate = (date: string) => {
            return new Date(date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        };

        const differences = getFeatureDifferences(subscription.plan, pendingDowngrade.targetPlan);

        return (
            <Card className="downgrade-banner border-warning mb-4">
                <Card.Header>
                    <div className="d-flex align-items-center">
                        <ArrowDown size={20} className="text-warning-600 me-2" />
                        <h6 className="mb-0 text-warning-600">Plan Downgrade Requested</h6>
                    </div>
                </Card.Header>
                <Card.Body className="p-4">
                    <div className="mb-4">
                        <div className="d-flex align-items-center text-warning-600 mb-3">
                            <AlertTriangle size={20} className="me-2" />
                            <p className="mb-0 fw-medium">
                                Your plan will change from {subscription.plan.name} to {pendingDowngrade.targetPlan.name}
                            </p>
                        </div>

                        <div className="d-flex align-items-center text-muted">
                            <Calendar size={20} className="me-2" />
                            <p className="mb-0">Changes will take effect on {formatDate(pendingDowngrade.effectiveDate)}</p>
                        </div>
                    </div>

                    {differences.length > 0 && (
                        <Alert variant="warning" className="warning-alert">
                            <div className="d-flex align-items-start">
                                <AlertCircle size={20} className="me-2 mt-1 text-warning-600" />
                                <div>
                                    <p className="fw-medium mb-2 text-warning-700">The following changes will occur:</p>
                                    <ul className="ps-3">
                                        {differences.map((difference, index) => (
                                            <li key={index} className="text-warning-600">{difference}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </Alert>
                    )}

                    <div className="d-flex justify-content-end mt-4">
                        <button
                            onClick={(e) => {
                                e.preventDefault(); // Prevent any default form submission
                                console.log('Cancel Change button clicked', {
                                    isPendingDowngrade: !!pendingDowngrade,
                                    isProcessing
                                });
                                handleCancelDowngrade();
                            }}
                            className="btn btn-outline-warning"
                            disabled={isProcessing}
                            type="button"
                        >
                            {isProcessing ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"
                                          aria-hidden="true"/>
                                    Processing...
                                </>
                            ) : (
                                'Cancel Change'
                            )}
                        </button>
                    </div>
                </Card.Body>
            </Card>
        );
    };

    const renderFeatureList = (plan: Plan) => {

        const allFeatures = [
            {
                name: `${plan.features.conversations_per_month.toLocaleString()} conversations/month`,
                available: true
            },
            {
                name: `${plan.features.storage_gb}GB storage`,
                available: true
            },
            {
                name: `${plan.features.knowledge_bases} knowledge base${plan.features.knowledge_bases > 1 ? 's' : ''}`,
                available: true
            },
            {
                name: `${plan.features.data_sources} data sources`,
                available: true
            },
            {
                name: `${plan.features.indexed_pages.toLocaleString()} indexed pages`,
                available: true
            },
            {
                name: plan.features.support_level,
                available: true
            },
            {
                name: 'REST API Access',
                available: plan.features.api_access === 'rest' || plan.features.api_access === 'full'
            },
            {
                name: 'Full API Access',
                available: plan.features.api_access === 'full'
            }
        ];

        return (
            <ul className="list-unstyled mb-4">
                {allFeatures.map((feature, index) => (
                    <li
                        key={index}
                        className={`mb-2 d-flex align-items-center ${
                            !feature.available ? 'text-muted text-decoration-line-through' : ''
                        }`}
                    >
                        <Check
                            size={16}
                            className={`me-2 ${feature.available ? 'text-success' : 'text-gray-300'}`}
                        />
                        {feature.name}
                    </li>
                ))}
            </ul>
        );
    };

    if (!auth?.user) return null;

    return (
            <>
                <div className="subscription-page page-container">
                    <div className="mb-4">
                        <h5 className="mb-3">Subscription Management</h5>
                        {loading ? (
                                <ContentSkeleton />
                            ) : error ? (
                            <Alert variant="danger" className="d-flex align-items-center">
                                <AlertCircle size={20} className="me-2"/>
                                {error}
                            </Alert>
                        ) : (
                            <div className="mb-4">
                                {subscription && (
                                    pendingCancellation ? (
                                        renderPendingCancellation()
                                    ) : pendingDowngrade ? (
                                        renderPendingDowngrade()
                                    ) : null
                                )}
                                {subscription && (
                                    <Card className="border-0 shadow-sm mb-4">
                                        <Card.Body className="p-4">
                                            <div className="d-flex align-items-center mb-4">
                                                <CreditCard size={24} className="text-primary me-2"/>
                                                <h6 className="mb-0">Current Plan: {subscription?.plan?.name || 'No Plan'}</h6>
                                            </div>

                                            <div className="row align-items-center">
                                                <div className="col-md-6">
                                                    <p className="mb-2 d-flex align-items-center">
                                                        Status:
                                                        <Badge
                                                            bg={
                                                                subscription.status === 'active' ? 'success' :
                                                                    subscription.status === 'past_due' ? 'warning' :
                                                                        'danger'
                                                            }
                                                            className="ms-2"
                                                        >
                                                            {subscription.status}
                                                        </Badge>
                                                    </p>
                                                    <p className="mb-0">
                                                        {subscription?.current_period_start ? (
                                                            <>
                                                                <span className="fw-medium">
                                                                    {subscription.plan.billing_interval === 'monthly' ? 'Monthly' : 'Yearly'} Period:
                                                                </span>{' '}
                                                                {new Date(subscription.current_period_start).toLocaleDateString('en-US', {
                                                                    year: 'numeric',
                                                                    month: 'long',
                                                                    day: 'numeric'
                                                                })} - {new Date(subscription.current_period_end).toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'long',
                                                                day: 'numeric'
                                                            })}
                                                            </>
                                                        ) : 'N/A'}
                                                    </p>
                                                </div>
                                                <div className="col-md-6 d-flex justify-content-end">
                                                    <button
                                                        onClick={handleCancelSubscription}
                                                        disabled={pendingDowngrade !== null || pendingCancellation !== null || isProcessing}
                                                        className="btn btn-outline-danger"
                                                        type="button"
                                                    >
                                                        {isProcessing ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"/>
                                                                Processing...
                                                            </>
                                                        ) : (
                                                            'Cancel Subscription'
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                )}

                                <Card className="border-0 shadow-sm">
                                    <Card.Body className="p-4">
                                        <div className="d-flex align-items-center mb-4">
                                            <Package size={24} className="text-primary me-2"/>
                                            <h6 className="mb-0">Available Plans</h6>
                                        </div>

                                        <div className="row g-4">
                                            {plans.map((plan) => {
                                                const isPendingDowngrade = pendingDowngrade?.targetPlan.id === plan.id;
                                                const isCurrentPlan = selectedPlan === plan.id;

                                                return (
                                                    <div key={plan.id} className="col-md-3">
                                                        <Card
                                                            className={`h-100 ${
                                                                isCurrentPlan ? 'border-primary' : ''
                                                            }`}
                                                        >
                                                            <Card.Body className="p-4 d-flex flex-column h-100">
                                                                <div>
                                                                    <h5 className="mb-2">{plan.name}</h5>
                                                                    <p className="text-muted mb-4">{plan.description}</p>
                                                                    <div className="mb-4">
                                                                        <h3 className="mb-0">
                                                                            ${plan.price}
                                                                            <span className="text-muted fs-6">/{plan.billing_interval}</span>
                                                                        </h3>
                                                                    </div>
                                                                    {renderFeatureList(plan)}
                                                                </div>

                                                                <button
                                                                    className={`btn w-100 mt-auto ${
                                                                        isCurrentPlan
                                                                            ? 'btn-secondary'
                                                                            : isPendingDowngrade
                                                                                ? 'btn-outline-warning change-scheduled'
                                                                                : 'btn-primary'
                                                                    }`}
                                                                    onClick={() => handlePlanSelect(plan.id)}
                                                                    disabled={isCurrentPlan || isPendingDowngrade || pendingCancellation !== null}
                                                                    type="button"
                                                                >
                                                                    {isCurrentPlan
                                                                        ? 'Current Plan'
                                                                        : isPendingDowngrade
                                                                            ? 'Change Scheduled'
                                                                            : 'Select Plan'}
                                                                </button>
                                                            </Card.Body>
                                                        </Card>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>
                        )}
                    </div>
                </div>

                {selectedNewPlan && subscription && (
                    <SubscriptionUpgradeModal
                        show={showUpgradeModal}
                        onHide={() => {
                            setShowUpgradeModal(false);
                            setSelectedNewPlan(null);
                        }}
                        currentSubscription={subscription}
                        selectedPlan={selectedNewPlan}
                        onUpgradeComplete={fetchSubscriptionData}
                    />
                )}
         </>
    );
};

export default SubscriptionPage;